/**
 * Hyperlinks auf den Folien.
 *
 * `Slide.hyperlinks` gibt es ab PowerPointApi 1.6. Welcher Text den Link
 * trägt, liefert erst 1.10 über `getLinkedTextRangeOrNullObject()`; davor
 * bleibt `text` leer.
 */

import { supportsPowerPointApi } from "./capabilities";
import { isPowerPointAvailable, PowerPointUnavailableError } from "./presentation";

export interface FolienLink {
  slideIndex: number;
  address: string;
  /** Text, der den Link trägt. `null`, wenn nicht lesbar oder ein Link auf einer Form. */
  text: string | null;
}

export async function readFolienLinks(): Promise<FolienLink[] | null> {
  if (!isPowerPointAvailable()) throw new PowerPointUnavailableError();
  if (!supportsPowerPointApi("1.6")) return null;
  const mitText = supportsPowerPointApi("1.10");

  try {
    return await PowerPoint.run(async (context) => {
      const slides = context.presentation.slides;
      slides.load("items");
      await context.sync();
      const sammlungen = slides.items.map((slide) => {
        const links = slide.hyperlinks;
        links.load("items/address");
        return links;
      });
      await context.sync();

      const texte = mitText
        ? sammlungen.map((links) =>
            links.items.map((link) => {
              const bereich = link.getLinkedTextRangeOrNullObject();
              bereich.load("text,isNullObject");
              return bereich;
            }),
          )
        : null;
      if (texte) await context.sync();

      return sammlungen.flatMap((links, slideIndex) =>
        links.items.map((link, i) => {
          const bereich = texte?.[slideIndex][i];
          return {
            slideIndex,
            address: typeof link.address === "string" ? link.address : "",
            text: bereich && !bereich.isNullObject ? (bereich.text ?? "").trim() : null,
          };
        }),
      );
    });
  } catch {
    return null;
  }
}
